import { useEffect, useState, useCallback } from "react";
import { opApi, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { CheckCircle2, Circle, Copy, ExternalLink } from "lucide-react";

const copy = (v) => { navigator.clipboard?.writeText(v); toast.success("Copied"); };

function LinkRow({ label, url, testid }) {
  return (
    <div className="flex items-center gap-2 text-xs bg-secondary px-3 py-2 mt-2" data-testid={testid}>
      <span className="text-muted-foreground whitespace-nowrap">{label}</span>
      <a href={url} target="_blank" rel="noreferrer" className="text-primary truncate flex-1">{url}</a>
      <button onClick={() => copy(url)} data-testid={`${testid}-copy`}><Copy className="h-3.5 w-3.5" /></button>
    </div>
  );
}

// Guided "Get found on Google" checklist (Google Business Profile + SEO + review requests).
export function GoogleSetup() {
  const [d, setD] = useState(null);
  const [f, setF] = useState({ profile_url: "", review_url: "" });
  const load = useCallback(() => opApi.get("/tenant/google-setup").then(({ data }) => { setD(data); setF({ profile_url: data.gbp.profile_url, review_url: data.gbp.review_url }); }).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  if (!d) return null;
  const save = (b) => opApi.patch("/tenant/google-setup", b).then(({ data }) => { setD(data); toast.success("Saved"); }).catch((e) => toast.error(apiError(e.response?.data?.detail)));
  const extra = {
    profile: <div className="flex gap-2 mt-2"><Input className="rounded-none" placeholder="https://g.page/your-business or Maps link" value={f.profile_url} onChange={(e) => setF({ ...f, profile_url: e.target.value })} data-testid="gbp-profile-input" /><Button className="rounded-none" onClick={() => save({ profile_url: f.profile_url })} data-testid="gbp-profile-save">Save</Button><a href="https://business.google.com" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-primary whitespace-nowrap" data-testid="gbp-open-google"><ExternalLink className="h-3 w-3" /> Open Google</a></div>,
    link: <><LinkRow label={d.link_field} url={d.action_url} testid="gbp-action-link" /><label className="flex items-center gap-2 mt-2 text-xs"><Switch checked={d.gbp.link_added} onCheckedChange={(v) => save({ link_added: v })} data-testid="gbp-link-added" /> I've added it to my Google profile</label></>,
    website: <><LinkRow label="Search-friendly page" url={d.seo_url} testid="gbp-seo-link" /><label className="flex items-center gap-2 mt-2 text-xs"><Switch checked={d.gbp.website_added} onCheckedChange={(v) => save({ website_added: v })} data-testid="gbp-website-added" /> Done</label></>,
    reviews: <><div className="flex gap-2 mt-2"><Input className="rounded-none" placeholder="https://g.page/r/…/review" value={f.review_url} onChange={(e) => setF({ ...f, review_url: e.target.value })} data-testid="gbp-review-input" /><Button className="rounded-none" onClick={() => save({ review_url: f.review_url })} data-testid="gbp-review-save">Save</Button></div>
      <label className="flex items-center gap-2 mt-2 text-xs"><Switch checked={d.gbp.review_requests} onCheckedChange={(v) => save({ review_requests: v })} data-testid="gbp-review-toggle" /> Email clients a review request after a completed visit</label></>,
  };
  return (
    <div className="space-y-1" data-testid="google-setup">
      <div className="text-sm mb-3" data-testid="google-setup-progress"><strong>{d.done} of {d.total}</strong> steps done</div>
      <ol className="border border-border divide-y">
        {d.steps.map((s, i) => (
          <li key={s.key} className="p-4 flex gap-3" data-testid={`gbp-step-${s.key}`}>
            {s.done ? <CheckCircle2 className="h-5 w-5 text-[hsl(var(--success))] shrink-0" /> : <Circle className="h-5 w-5 text-muted-foreground shrink-0" />}
            <div className="flex-1 text-sm"><div className="font-semibold">{i + 1}. {s.title}</div><div className="text-xs text-muted-foreground mt-0.5">{s.detail}</div>{extra[s.key]}</div>
          </li>
        ))}
      </ol>
    </div>
  );
}

export function GoogleSetupNudge({ onOpen }) {
  const [d, setD] = useState(null);
  const [hidden, setHidden] = useState(() => localStorage.getItem("mc_gbp_nudge_hidden") === "1");
  useEffect(() => { if (!hidden) opApi.get("/tenant/google-setup").then(({ data }) => setD(data)).catch(() => {}); }, [hidden]);
  if (hidden || !d || d.done >= d.total) return null;
  return (
    <div className="border border-border px-4 py-3 mb-6 flex items-center justify-between gap-3 flex-wrap" data-testid="gbp-nudge">
      <span className="text-sm"><strong>Get found on Google</strong> · {d.done} of {d.total} steps done. Let customers book or order straight from Google Search &amp; Maps.</span>
      <div className="flex gap-2">
        <Button size="sm" variant="ghost" className="rounded-none" onClick={() => { localStorage.setItem("mc_gbp_nudge_hidden", "1"); setHidden(true); }} data-testid="gbp-nudge-hide">Later</Button>
        <Button size="sm" className="rounded-none" onClick={onOpen} data-testid="gbp-nudge-open">Continue setup</Button>
      </div>
    </div>
  );
}
